import {AttrsSchema, Component} from 'panel';
import 'regenerator-runtime/runtime'; // required by gcs-browser-upload
import Upload from 'gcs-browser-upload';

import {sleep} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {lookupTablesStore, registerTempLookupTableMapping} from 'common/report/stores/lookup-tables';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {abbreviateNumber} from 'common/util';

import type {PropertyItem} from '../query-block/properties-menu/types';
import {template} from './template';
import {trackCompleteMapping, trackInfoClick, trackSelectTable, trackUploadComplete} from './tracking';
import css from './index.styl?inline';
import {propIneligibleForMappingReason} from './util';
import {
  getLookupTableUploadStatus,
  getLookupTableUploadUrl,
  processLookupTable,
  ProcessLookupTableCompletedResults,
} from './lookup-tables-api';

export type MapLookupTableModalType = `import` | `temporary-map`;

interface LookupTableSectionItem {
  label: string;
  dataGroupId: string;
}

/** A PropertyItem with the `value` (aka propertyName) key required.
 *
 * `value`, `resourceType`, `type`, and `propertyDefaultType` are required */
export type PropertyItemWithValue = Omit<PropertyItem, 'label'> & {value: string | string[]};

type MapLookupTableModalState = {
  selectedLookupTable?: LookupTableSectionItem;
  selectedProperty?: PropertyItemWithValue | EmptyObj;
  lookupTables: LookupTableSectionItem[];
  hideSelect: boolean;
  selectTableErrorMessage?: string;
  fileProcessingDescription?: string;
  uploadedFilename?: string;
  invalidPropertyErrorMessage?: string;
  savePending: boolean;
};

export type MapLookupTableModalAttrs = {
  type: MapLookupTableModalType;
  open: boolean;
  'project-id': number;
  'workspace-id': number;
  'user-id': number;
  'project-has-people': boolean;
  'selected-property'?: PropertyItemWithValue;
  'event-names'?: string[];
  'disable-property-selection'?: boolean;
  elref?: string;
};

type MapLookupTableModalJsxProps = {
  attrs: MapLookupTableModalAttrs;
};

export class MapLookupTableModal extends Component<MapLookupTableModalState, MapLookupTableModalAttrs> {
  static get attrsSchema(): AttrsSchema<MapLookupTableModalAttrs> {
    return {
      type: {type: `string`, description: `MapLookupTableModalType`},
      open: `boolean`,
      'project-id': `number`,
      'workspace-id': `number`,
      'user-id': `number`,
      'project-has-people': `boolean`,
      'selected-property': {type: `json`, default: null},
      'event-names': {type: `json`, default: null},
      'disable-property-selection': `boolean`,
      elref: `string`,
    };
  }
  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        selectedLookupTable: null,
        selectedProperty: null,
        lookupTables: [],
        hideSelect: false,
        selectTableErrorMessage: null,
        fileProcessingDescription: null,
        uploadedFilename: null,
        invalidPropertyErrorMessage: null,
        savePending: false,
      },
      helpers: {
        getModalTitle: () =>
          this.attr(`type`) === `temporary-map` ? `Create Lookup Table Mapping` : `Import Lookup Table`,
        getSelectedLabel: () => this.state.selectedLookupTable?.label || `Select Existing`,
        getLookupTableSections: () => {
          return [{items: this.state.lookupTables}];
        },
        getFileUploadBlockAttrs: () => {
          const {fileProcessingDescription, uploadedFilename} = this.state;
          return {
            label: `Import CSV`,
            'enable-delete': true,
            disabled: this.state.savePending,
            ...(fileProcessingDescription ? {'processing-description': fileProcessingDescription} : {}),
            ...(uploadedFilename ? {'completed-filename': uploadedFilename} : {}),
          };
        },
        getQueryBlockAttrs: () => {
          return {
            type: `properties`,
            theme: `select`,
            'project-id': this.attr(`project-id`),
            'workspace-id': this.attr(`workspace-id`),
            'user-id': this.attr(`user-id`),
            'project-has-people': this.attr(`project-has-people`),
            'event-names': this.attr(`event-names`),
            selected: this.state.selectedProperty,
            disabled: this.attr(`disable-property-selection`) || this.state.savePending,
            'include-custom-properties': `persisted-only`,
            removable: this.attr(`type`) === `import` && Boolean(this.state.selectedProperty),
            tabindex: 0,
          };
        },
        selectLookupTable: (ev) => {
          this.update({selectedLookupTable: ev.detail.item, selectTableErrorMessage: null});
          trackSelectTable(this);
        },
        handleModalClose: (ev) => {
          if (ev.detail.state === `closed`) {
            this.helpers.close();
          }
        },
        close: () => {
          this.dispatchEvent(new CustomEvent(`close`));
        },

        uploadLookupTableCSV: async (ev) => {
          // TODO(j) this is based on `uploadLookupTable` in iron/apps/lexicon/src/lexicon-app/index.js:524. Remove that when
          // this component replaces the current upload flow in Lexicon.
          this.update({
            selectedLookupTable: null,
            selectTableErrorMessage: null,
            fileProcessingDescription: `Uploading file...`,
          });

          const {file} = ev.detail;
          const {name: fileName, size: fileSize, type: fileType} = file;
          const maxMB = 500;
          const csvMaxFileSize = 1024 * 1024 * maxMB;
          if (fileSize > csvMaxFileSize) {
            this.setUploadAbortedState(`Cannot upload files larger than ${maxMB} MB`);
            return;
          }

          try {
            const projectId = this.attr(`project-id`);
            const workspaceId = this.attr(`workspace-id`);
            const urlGetStartTime = performance.now();
            const urlResults = await getLookupTableUploadUrl(projectId, workspaceId, fileType);
            const {url, path, key} = urlResults;
            const upload = new Upload({
              id: fileName,
              url,
              file,
              contentType: fileType,
              chunkSize: 256 * 2000 * 1024, // 500Mb chunks, must be a multiple of 256Kb.
              onChunkUpload: (info) => {
                this.update({
                  fileProcessingDescription: `Uploading file (${abbreviateNumber(
                    (info.uploadedBytes / info.totalBytes) * 100,
                  )}%)`,
                });
              },
            });

            const uploadStartTime = performance.now();
            await upload.start();

            this.update({fileProcessingDescription: `Processing file...`});
            const processStartTime = performance.now();
            const processResults = await processLookupTable(projectId, workspaceId, key, fileName, path, null);

            let results: ProcessLookupTableCompletedResults;
            if (`uploadId` in processResults) {
              let complete = false;
              // If we are uploading asynchronously poll until the table is ready.
              while (!complete) {
                const statusResp = await getLookupTableUploadStatus(projectId, workspaceId, processResults.uploadId);
                if (statusResp.uploadStatus === `SUCCESS`) {
                  results = statusResp.result;
                  complete = true;
                } else if (statusResp.uploadStatus === `FAILURE`) {
                  this.setUploadAbortedState(`Sorry, an error occurred. Please try importing again`);
                  return;
                }
                await sleep(10000); // 10 Seconds
              }
            } else {
              results = processResults;
            }

            const dataGroupId = results.id;
            const finishTime = performance.now();
            const dedupDropped = results.file - results.deduped;
            const importDropped = results.deduped - results.imported;

            let selectTableErrorMessage = null;
            if (importDropped > 0) {
              selectTableErrorMessage = `${results.file - results.imported} rows were dropped`;
            } else if (dedupDropped > 0) {
              selectTableErrorMessage = `${dedupDropped} rows were dropped as duplicate rows`;
            }
            this.update({
              selectTableErrorMessage,
              uploadedFilename: fileName,
              hideSelect: true,
              fileProcessingDescription: null,
              selectedLookupTable: {dataGroupId, label: fileName},
            });
            addToast({type: ToastType.SUCCESS, message: `Imported new lookup table`});
            this.dispatchEvent(new CustomEvent(`newTableUploaded`, {detail: {dataGroupId}}));

            trackSelectTable(this, true);
            trackUploadComplete({
              el: this,
              fileSize,
              isNewLookupTable: true,
              numberOfRowsProcessed: results.imported,
              numberOfRowsUploaded: results.file,
              uploadStatus: dedupDropped > 0 ? `Warning` : `Success`,
              uploadStatusReason: dedupDropped > 0 ? `De-duplicated rows` : null,
              timeToGetUrl: (uploadStartTime - urlGetStartTime) / 1000,
              timeToUpload: (processStartTime - uploadStartTime) / 1000,
              timeToProcess: (finishTime - processStartTime) / 1000,
              totalTime: (finishTime - urlGetStartTime) / 1000,
              numColumns: results.columns,
            });

            lookupTablesStore.refresh();
          } catch (err) {
            console.error(err);
            this.setUploadAbortedState(`Sorry, an error occurred. Please try importing again.`);
          }
        },

        handleDeleteUpload: () => {
          const projectId = this.attr(`project-id`);
          const workspaceId = this.attr(`workspace-id`);
          const dataGroupId = this.state.selectedLookupTable.dataGroupId;
          lookupTablesStore
            .delete(dataGroupId, {projectId, workspaceId, dataGroupIds: [dataGroupId]})
            .then(() => {
              this.dispatchEvent(new CustomEvent(`tableDeleted`, {detail: {dataGroupId}}));
              addToast({type: ToastType.DESTRUCTIVE, message: `Deleted new lookup table`});
            })
            .catch(() => {
              addToast({type: ToastType.WARNING, message: `Error deleting lookup table`});
            });
          this.update({
            uploadedFilename: null,
            selectedLookupTable: null,
            selectTableErrorMessage: null,
          });
          // Delaying this update prevents a flicker of horizontal layout shift that happens when
          // the select gets shown while `mp-file-uploader` is still displaying uploadedFilename
          requestAnimationFrame(() => {
            this.update({hideSelect: false});
          });
        },

        handleSelectProperty: (ev) => {
          let invalidPropertyErrorMessage;
          if (this.attr(`type`) === `temporary-map`) {
            invalidPropertyErrorMessage = propIneligibleForMappingReason(ev.detail.rawItem, true);
          } else {
            invalidPropertyErrorMessage = propIneligibleForMappingReason(ev.detail.rawItem);
          }
          this.update({selectedProperty: ev.detail.item, invalidPropertyErrorMessage});
        },

        handleRemoveProperty: () => {
          this.update({selectedProperty: null, invalidPropertyErrorMessage: null});
        },

        shouldDisableConfirm: (): boolean => {
          return (
            !this.state.selectedLookupTable ||
            (this.attr(`type`) === `temporary-map` && !this.state.selectedProperty?.resourceType) ||
            Boolean(this.state.invalidPropertyErrorMessage) ||
            this.state.savePending
          );
        },

        getConfirmButtonLabel: () => (this.attr(`type`) === `temporary-map` ? `Apply` : `Save`),

        handleConfirmClick: () => {
          if (this.attr(`type`) === `temporary-map`) {
            this.createTemporaryMapping();
          } else if (this.attr(`type`) === `import`) {
            // ideally this component would handle saving, but we first need to extract the api client out of lexicon
            this.update({savePending: true});
            this.dispatchEvent(
              new CustomEvent(`save`, {
                detail: {
                  selectedProperty: this.state.selectedProperty,
                  selectedDataGroupId: this.state.selectedLookupTable.dataGroupId,
                },
              }),
            );
          }
        },

        trackInfoClick: () => trackInfoClick(this),
      },
    };
  }

  public saveAborted() {
    this.update({savePending: false});
  }

  private createTemporaryMapping() {
    const property = this.state.selectedProperty as PropertyItemWithValue;
    const propertyName = Array.isArray(property.value) ? property.value[0] : property.value;
    const succeeded = registerTempLookupTableMapping(
      property.resourceType,
      propertyName,
      this.state.selectedLookupTable.dataGroupId,
    );
    if (succeeded) {
      addToast({
        message: `Applied temporary lookup table mapping`,
        type: ToastType.SUCCESS,
      });
      trackCompleteMapping(this, property);
      this.helpers.close();
    }
  }

  private resetState() {
    const {lookupTables, ...defaultStateWithoutTables} = this.config.defaultState;
    this.update({...defaultStateWithoutTables});
  }

  private setUploadAbortedState(selectTableErrorMessage) {
    this.update({
      selectTableErrorMessage,
      fileProcessingDescription: null,
    });
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected-property`) {
      // assumes property is validated via propIneligibleForMappingReason before the attribute is set
      this.update({selectedProperty: this.attr(`selected-property`)});
    } else if (attr === `open` && !this.attr(`open`)) {
      requestAnimationFrame(() => this.resetState());
    }
  }

  private lookupTablesStoreCallback;
  connectedCallback() {
    super.connectedCallback();

    this.lookupTablesStoreCallback = () => {
      const lookupTables = Object.values(lookupTablesStore.value).map((table) => {
        return {dataGroupId: table.id, label: table.name};
      });
      this.update({lookupTables});
    };

    // TODO(j) may need to fetch when in Lexicon
    lookupTablesStore.subscribe(this.lookupTablesStoreCallback);
    if (lookupTablesStore.hasFetchedValue()) {
      this.lookupTablesStoreCallback();
    }
  }

  disconnectedCallback() {
    lookupTablesStore.unsubscribe(this.lookupTablesStoreCallback);
    this.lookupTablesStoreCallback = null;

    super.disconnectedCallback();
  }
}

export const MapLookupTableModalEl = defineAndMakeJsxFactory<MapLookupTableModalJsxProps>(
  `mp-map-lookup-table-modal`,
  MapLookupTableModal,
);
